import React, { useState, useEffect, SyntheticEvent } from 'react';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { Link as ReactRouterLink } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { useAppContext } from '../../../utils/content.util';
import { onError } from '../../../utils/error.util';
import { Dealer } from '../../../models/dealer.model';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import { archiveDealer, getDealers, unarchiveDealer } from '../../../services/dealers.service';
import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import config from '../../../config';
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import SearchBox from '../../../components/admin/search-box/search-box.component';
import { hasPermission, Permission } from '../../../models/role.model';
import { AwsCognitoUser } from '../../../models/user.model';

const LIMIT = 100;

function showArchiveButton(user: AwsCognitoUser) {
  return hasPermission(user, Permission.ARCHIVE_DEALER);
}

function showAddDealerButton(user: AwsCognitoUser) {
  return hasPermission(user, Permission.CREATE_DEALER);
}

function DealerTable({
  data,
  page,
  rowsPerPage = LIMIT,
  handleShowArchiveModal,
  handleShowUnarchiveModal,
  handleChangeRowsPerPage,
  handleChangePage,
  copyDealerLink,
  currentUser,
  lastKey,
}: any) {
  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell> </TableCell>
              <TableCell>id</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>City</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Zipcode</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell> </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row: any) => (
              <DealerTableRow
                key={row.id}
                row={row}
                handleShowArchiveModal={handleShowArchiveModal}
                handleShowUnarchiveModal={handleShowUnarchiveModal}
                copyDealerLink={copyDealerLink}
                currentUser={currentUser}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

function DealerTableRow({
  row,
  handleShowArchiveModal,
  handleShowUnarchiveModal,
  copyDealerLink,
  currentUser,
}: {
  row: Dealer;
  handleShowArchiveModal: any;
  handleShowUnarchiveModal: any;
  copyDealerLink: any;
  currentUser: any;
}) {
  return (
    <TableRow selected={row.isArchived}>
      <TableCell align="left">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Copy Dealer Link">
            <ContentCopyIcon
              color="primary"
              fontSize="small"
              onClick={() => copyDealerLink(row.id)}
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
        </Box>
      </TableCell>

      <TableCell>{row.id}</TableCell>
      <TableCell>
        <ReactRouterLink key={row.id} to={`/dealers/${row.id}`}>
          <span>{row.companyName}</span>
        </ReactRouterLink>
      </TableCell>
      <TableCell>{row.address.addressLine1}</TableCell>
      <TableCell>{row.address.city}</TableCell>
      <TableCell>{row.address.state}</TableCell>
      <TableCell>{row.address.zipCode}</TableCell>
      <TableCell>{row.phone}</TableCell>
      <TableCell align="left">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {!row.isArchived && currentUser && showArchiveButton(currentUser) ? (
            <Tooltip title="Archive">
              <ReactRouterLink onClick={() => handleShowArchiveModal(row.id)} to="">
                <ArchiveIcon color="error" fontSize="small" />
              </ReactRouterLink>
            </Tooltip>
          ) : undefined}
          {row.isArchived && currentUser && showArchiveButton(currentUser) ? (
            <Tooltip title="Unarchive">
              <ReactRouterLink onClick={() => handleShowUnarchiveModal(row.id)} to="">
                <UnarchiveIcon fontSize="small" />
              </ReactRouterLink>
            </Tooltip>
          ) : undefined}
        </Box>
      </TableCell>
    </TableRow>
  );
}

export const copyDealerLink = async (dealerId: string, setOpenAlertCopy?: any) => {
  try {
    const dealerLink = `${config.app.web_url}id/${dealerId}`;
    await navigator.clipboard.writeText(dealerLink);
    setOpenAlertCopy && setOpenAlertCopy(true);
  } catch (err) {
    console.error('Failed to copy: ', err);
  }
};

export default function DealerContainer() {
  const { isAuthenticated, currentUser } = useAppContext();

  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [lastKey, setLastKey] = useState<string>('');
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [currentCursor, setCurrentCursor] = useState<string>('');

  const [isLoading, setIsLoading] = useState(true);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isUnarchiving, setIsUnarchiving] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
  const [currentItemId, setCurrentItemId] = useState<string>('');
  const [openAlertCopy, setOpenAlertCopy] = React.useState(false);
  const [showArchivedDealers, setShowArchivedDealers] = useState(false);
  const [showUnarchiveModal, setShowUnarchiveModal] = useState(false);
  const [shouldReload, setShouldReload] = useState(false);

  const handleClickOpenArchive = (itemId: string) => {
    setCurrentItemId(itemId);
    setShowArchiveModal(true);
  };

  const handleClickOpenUnarchive = (itemId: string) => {
    setCurrentItemId(itemId);
    setShowUnarchiveModal(true);
  };

  const handleCloseArchive = () => setShowArchiveModal(false);
  const handleCloseUnarchive = () => setShowUnarchiveModal(false);

  const handleArchive = async () => {
    setIsArchiving(true);
    try {
      await archiveDealer(currentItemId);
    } catch (err) {
      alert(err);
    } finally {
      setShowArchiveModal(false);
      setIsArchiving(false);
      onLoad();
    }
  };

  const handleUnarchive = async () => {
    setIsUnarchiving(true);
    try {
      await unarchiveDealer(currentItemId);
    } catch (err) {
      alert(err);
    } finally {
      setShowUnarchiveModal(false);
      setIsUnarchiving(false);
      onLoad();
    }
  };

  async function onLoad(name = '', cursor = '') {
    if (!isAuthenticated) {
      return;
    }

    setDealers([]);
    setIsLoading(true);
    try {
      const response = await getDealers({ name, archived: showArchivedDealers, lastKey: cursor, limit: LIMIT });
      setDealers(response.items);
      setLastKey(response.lastKey);
    } catch (e) {
      onError(e);
    }

    setIsLoading(false);
  }

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (search: string) => {
    onLoad(search);
  };

  useEffect(() => {
    if (shouldReload) {
      onLoad();
      setShouldReload(false); // Reset the flag
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldReload]);

  const handleSearchClear = () => {
    setShowArchivedDealers(false);
    setShouldReload(true); // Set the flag to true
  };

  function renderDealers() {
    return (
      <>
        <div className="dealers">
          <h2 className="pb-2 mt-4 mb-3 border-bottom">Dealers</h2>
        </div>
        <div>
          <div className="pb-2 mt-4 mb-3">
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
              {currentUser && showAddDealerButton(currentUser) ? (
                <ReactRouterLink to={'/dealers/new'}>
                  <Button variant="contained">New Dealer</Button>
                </ReactRouterLink>
              ) : undefined}

              <SearchBox
                placeholder="Dealer name starts with"
                handleSubmit={handleSearch}
                handleClearAll={handleSearchClear}
              />
              <FormControlLabel
                control={<Checkbox />}
                label="Archived"
                checked={showArchivedDealers}
                onChange={(e) => setShowArchivedDealers(!showArchivedDealers)}
              />
            </Box>
          </div>
        </div>
      </>
    );
  }

  const handleCloseAlertCopy = (event: Event | SyntheticEvent<any, Event>, reason: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenAlertCopy(false);
  };

  const handleNext = () => {
    if (lastKey) {
      // Save the current cursor so we can go back later.
      setCursorHistory([...cursorHistory, currentCursor]);
      // Update the current cursor to the one your API provided for the next page.
      setCurrentCursor(lastKey);
      // Load the next page.
      onLoad('', lastKey);
    }
  };

  const handlePrev = () => {
    if (cursorHistory.length > 0) {
      const newHistory = [...cursorHistory];
      const previousCursor = newHistory.pop() || '';
      setCursorHistory(newHistory);
      // Update the current cursor to the one that was used to load the previous page.
      setCurrentCursor(previousCursor);
      // setCurrentPage(currentPage - 1);  // if you need to update currentPage
      onLoad('', previousCursor);
    }
  };

  return (
    <React.Fragment>
      <Grid size={{ xs: 12 }}>
        <div className="Dealers">
          {renderDealers()}
          {isLoading ? (
            <CircularProgressComponent />
          ) : (
            <DealerTable
              data={dealers}
              page={0}
              rowsPerPage={LIMIT}
              handleShowArchiveModal={handleClickOpenArchive}
              handleShowUnarchiveModal={handleClickOpenUnarchive}
              handleChangeRowsPerPage={() => {}}
              handleChangePage={() => {}}
              copyDealerLink={copyDealerLink}
              currentUser={currentUser}
              lastKey={lastKey}
            />
          )}
          {!isLoading && dealers.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 10 }}>
              <Button onClick={handlePrev} disabled={cursorHistory.length === 0}>
                Previous
              </Button>
              {/* <Typography variant="body2">Page {currentPage + 1}</Typography> */}
              <Button onClick={handleNext} disabled={!lastKey}>
                Next
              </Button>
            </Box>
          )}
        </div>
      </Grid>

      <Snackbar open={openAlertCopy} autoHideDuration={6000} onClose={handleCloseAlertCopy}>
        <Alert severity="success" sx={{ width: '100%' }}>
          Copied to clipboard!
        </Alert>
      </Snackbar>

      <Dialog
        open={showArchiveModal}
        onClose={(_event, reason) => {
          if (reason === 'escapeKeyDown') return;
          handleCloseArchive();
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Are you sure you want to archive this?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleArchive} disabled={isArchiving}>
            Archive
          </Button>
          <Button onClick={handleCloseArchive} disabled={isArchiving} autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showUnarchiveModal}
        onClose={(_event, reason) => {
          if (reason === 'escapeKeyDown') return;
          handleCloseUnarchive();
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Are you sure you want to unarchive this?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUnarchive} disabled={isUnarchiving}>
            Unarchive
          </Button>
          <Button onClick={handleCloseUnarchive} disabled={isUnarchiving} autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
